/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import { getFunctions, httpsCallable } from "firebase/functions";
import { initializeApp } from "firebase/app";
import { getFirestore, setDoc, Firestore, onSnapshot, collection, getDocs, doc, QueryDocumentSnapshot, deleteDoc, updateDoc, getDoc } from "firebase/firestore";
import { deleteUser, EmailAuthProvider, FacebookAuthProvider, getAuth, GoogleAuthProvider, onAuthStateChanged, reauthenticateWithCredential, reauthenticateWithPopup, User } from "firebase/auth";
import { getStorage, ref, uploadString, deleteObject, getDownloadURL } from "firebase/storage";
import Monster from "../model/Monster";
import NpcData from "../model/NpcData";
import Spell from "../model/Spell";
import AuthService from "./AuthService";


// Firebase configuration
const firebaseConfig = {
  apiKey:           "AIzaSyBZ0mwOpv-iMru5NOSuJRIPCk4BkPAsqdA",
  authDomain:       "monster-forge.firebaseapp.com",
  projectId:        "monster-forge",
  storageBucket:    "monster-forge.firebasestorage.app",
  messagingSenderId:"308739255556",
  appId:            "1:308739255556:web:e3af6908eeefd4105eb451",
  measurementId:    "G-LYFYW4HCER"
};

// Initialize Firebase app and services
const app = initializeApp(firebaseConfig);
const db: Firestore = getFirestore(app);
export const auth = getAuth(app);
const storage = getStorage(app);
const usernamesInUseByPrefix = new Map<string, string[]>();
const functions = getFunctions();
const checkAndRegisterUsername = httpsCallable(functions, "checkAndRegisterUsername");
const checkUsernameAvailable = httpsCallable(functions, "checkUsernameAvailable");

// Firestore Utility
class FirestoreService {
  private currentUser: User | null = null;
  private unsubscribeUserSnapshot: (() => void) | null = null;
  private unsubscribeMonstersSnapshot: (() => void) | null = null;

  constructor() {
    // Watch for auth state changes
    onAuthStateChanged(auth, (user) => {
      this.currentUser = user;
      if (!user) {
        // User logged out, clear snapshot listener
        this.clearSnapshotListener();
      }
    });
  }

  async deleteUserDataWithAuthentication(user: User, username: string, password: string, setErrors: (message: string) => void): Promise<boolean> {
    if (!user || !user.email) {
      console.error("No user is currently logged in.");
      return false;
    }

    try {
      // Step 1: Reauthenticate the user
      if (password) {
        const credential = EmailAuthProvider.credential(user.email.trim(), password);
        await reauthenticateWithCredential(user, credential);
      } else {
        let provider = null;
        if (user.providerData[0].providerId.toLowerCase() === 'google.com') {
          provider = new GoogleAuthProvider();
        } else if (user.providerData[0].providerId.toLowerCase() === 'facebook.com') {
          provider = new FacebookAuthProvider();
        }
        if (!provider) {
          console.error('no valid provider found to validate for account deletion');
          return false;
        }
        await reauthenticateWithPopup(user, provider);
      }
      console.log("Reauthentication successful.");

      const db = getFirestore();
      // Step 2: Delete user's subcollections (e.g., monsters) and their images
      const monstersCollectionRef = collection(db, "users", user.uid, "monsters");
      const monsterDocs = await getDocs(monstersCollectionRef);
      if (!monsterDocs.empty) {
        console.log(`Deleting ${monsterDocs.docs.length} monsters...`);
        // Delete images and documents in parallel
        await Promise.all(monsterDocs.docs.map(async (docSnap) => {
          // Delete the image first
          await this.deleteMonsterImage(docSnap.id);
          // Then delete the document
          await deleteDoc(docSnap.ref);
        }));
      }
      console.log("Monsters removed.");

      // Step 3: Delete the user documents
      await deleteDoc(doc(db, "users", user.uid));
      console.log("user doc removed.");
      if (username) {
        console.log("removing username.");
        await deleteDoc(doc(db, "usernames", username.toLowerCase()));
        console.log("username removed.");
      }
      
      // Step 4: Delete user from Firebase Authentication
      await deleteUser(user);
      console.log("User account deleted from Firebase Authentication.");
      return true;
    } catch (error) {
      console.error("Error deleting user:", error);
      AuthService.handleError("Deleting user data error", error, setErrors);
      return false;
    }
  };
  
  async updateBirthday(user: User, birthday: Date): Promise<boolean> {
    return this.updateData(user, { birthday });
  }
  async acceptTerm(user: User): Promise<boolean> {
    return this.updateData(user, { acceptedTerms: new Date() });
  }
  async updateData(user: User, data: any): Promise<boolean> {
    const userRef = doc(db, "users", user.uid);
    try {
      await updateDoc(userRef, data);
      console.log(`Updated Firestore with: ${data}`);
      return true;
    } catch (error) {
      console.error(`Error updating data ${data}:`, error);
      return false;
    }
  }
  
  async registerUsername(userId: string, username: string, birthday: Date): Promise<boolean> {
    try {
      console.log('calling checkAndRegisterUsername');
      const result: any = await checkAndRegisterUsername({ userId, username, birthday });
      console.log(result.data.message);
      return true;
    } catch (error: any) {
      console.error("Error:", error.message);
      return false;
    }
  }

  async isUsernameAvailable(username: string): Promise<boolean> {
    try {
      if (!username || username.length < 3) {
          return false;
      }
      const prefix = username.substring(0, 3).toLowerCase();
      const namesInUse = usernamesInUseByPrefix.get(prefix);
      if (namesInUse) {
        return !namesInUse.includes(username.toLowerCase());
      }
      console.log('calling checkUsernameAvailable');
      const result: any = await checkUsernameAvailable({ username });
      console.log(result.data.message);
      if (result.data.success) {
        usernamesInUseByPrefix.set(result.data.prefix, result.data.usernames);
        return !result.data.usernames.includes(username.toLowerCase());
      }
      return false;
    } catch (error: any) {
      console.error("Error:", error.message);
      return false;
    }
  }

  watchUserMonsters(spellsBySlug: Map<string, Spell>, onMonstersChanged: (data: Monster[]) => void): void {
    // If no user is logged in, immediately return an empty array
    if (!this.currentUser) {
      console.log("No user is logged in. Returning empty monsters array.");
      onMonstersChanged([]);
      return;
    }

    try {
      // Reference to the monsters subcollection
      const monstersCollectionRef = collection(db, "users", this.currentUser.uid, "monsters");

      // Set up the snapshot listener with error handling
      this.unsubscribeMonstersSnapshot = onSnapshot(
        monstersCollectionRef,
        (snapshot) => {
          try {
            const monsters: Monster[] = [];
            snapshot.docs.forEach((docSnapshot: QueryDocumentSnapshot) => {
              try {
                const jsonObject = docSnapshot.data();

                // Validate the JSON object before processing
                if (!jsonObject) {
                  console.warn(`Empty or invalid document data for document ID: ${docSnapshot.id}`);
                  return; // Skip this document
                }

                try {
                  // Convert the JSON to a Monster object
                  const mon = Monster.convertFromJson(jsonObject);
                  if (mon) {
                    // Process spells if available
                    if (mon.npcData && spellsBySlug) {
                      try {
                        NpcData.updateSpellsFromSlugs(mon.npcData, spellsBySlug);
                      } catch (spellError) {
                        console.error(`Error updating spells for monster ${mon.name}:`, spellError);
                      }
                    }
                    monsters.push(mon);
                  }
                } catch (parseError) {
                  console.error(`Error parsing monster from document ${docSnapshot.id}:`, parseError);
                }
              } catch (docError) {
                console.error(`Error processing document ${docSnapshot.id}:`, docError);
              }
            });

            // Update with the processed monsters
            onMonstersChanged(monsters);
          } catch (snapshotError) {
            console.error("Error processing monsters snapshot:", snapshotError);
            onMonstersChanged([]); // Return empty array on error
          }
        },
        (error) => {
          console.error("Error watching monster collection:", error);
          onMonstersChanged([]); // Return empty array on error
        }
      );
    } catch (setupError) {
      console.error("Error setting up monsters snapshot listener:", setupError);
      onMonstersChanged([]); // Return empty array on error
    }
  }

  /**
   * Register a watcher for changes to the user's data.
   * @param onUserDataChanged Callback to invoke when user data changes.
   */
  watchUserData(onUserDataChanged: (data: Record<string, any> | null) => void): boolean {
    if (!this.currentUser) {
      console.error("No user is logged in. Watcher will not be set.");
      onUserDataChanged(null); // Notify listener of no user
      return false;
    }

    const userRef = doc(db, "users", this.currentUser.uid);

    // Set up Firestore snapshot listener
    this.unsubscribeUserSnapshot = onSnapshot(
      userRef,
      (snapshot) => {
        if (snapshot.exists()) {
          onUserDataChanged(snapshot.data());
        } else {
          onUserDataChanged(null); // No data for the user
        }
      },
      (error) => {
        console.error("Error watching user data:", error);
        onUserDataChanged(null); // Notify listener of error
      }
    );
    return true;
  }

  static prepMonsterName(monsterName: string): string {
    // Only replace truly invalid characters for Firebase document IDs
    // Forward slash is the main invalid character to replace
    return monsterName.trim().replace(/\//g, '_');
  }

  /**
   * Upload a monster image to Firebase Storage and return the download URL.
   * @param monsterName The name of the monster
   * @param imageDataUrl The image data URL (must start with 'data:image/')
   * @returns Promise<string> The download URL for the uploaded image
   */
  async uploadMonsterImage(monsterName: string, imageDataUrl: string): Promise<string> {
    if (!this.currentUser) {
      throw new Error("No user is logged in.");
    }

    if (!imageDataUrl || !imageDataUrl.startsWith('data:image/')) {
      throw new Error("Invalid image data URL");
    }

    const validMonsterName = FirestoreService.prepMonsterName(monsterName);

    try {
      // Create a reference to the storage location
      // Path: /users/{userId}/images/{monsterName}.webp
      const imageRef = ref(storage, `users/${this.currentUser.uid}/images/${validMonsterName}.webp`);

      // Upload the image (uploadString handles data URLs automatically)
      const snapshot = await uploadString(imageRef, imageDataUrl, 'data_url');

      // Get the download URL
      const downloadURL = await getDownloadURL(snapshot.ref);

      console.log('Image uploaded successfully:', downloadURL);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  }

  /**
   * Delete a monster image from Firebase Storage.
   * @param monsterName The name of the monster
   */
  async deleteMonsterImage(monsterName: string): Promise<void> {
    if (!this.currentUser) {
      throw new Error("No user is logged in.");
    }

    const validMonsterName = FirestoreService.prepMonsterName(monsterName);

    try {
      // Create a reference to the storage location
      const imageRef = ref(storage, `users/${this.currentUser.uid}/images/${validMonsterName}.webp`);

      // Delete the image
      await deleteObject(imageRef);
      console.log('Image deleted successfully');
    } catch (error: any) {
      // If the file doesn't exist, that's okay (error code: storage/object-not-found)
      if (error.code === 'storage/object-not-found') {
        console.log('Image does not exist, nothing to delete');
      } else {
        console.error('Error deleting image:', error);
        throw error;
      }
    }
  }

  async deleteMonsterFromCollection(monsterName: string) {
    if (!this.currentUser) {
      console.error("No user is logged in.");
      return;
    }
    // Make sure the monster name is a valid document ID
    const validMonsterName = FirestoreService.prepMonsterName(monsterName);

    try {
      // Delete the monster image from Storage first
      await this.deleteMonsterImage(monsterName);

      // Reference to the monsters subcollection
      const monstersCollectionRef = collection(db, "users", this.currentUser.uid, "monsters");

      // Delete the monster document
      await deleteDoc(doc(monstersCollectionRef, validMonsterName));
      console.log("Monster deleted successfully.");
    } catch (error) {
      console.error("Error deleting monster data:", error);
    }
  };

  async addOrUpdateMonsterToCollection(monster: Monster, serializeFn: (mon: Monster) => Record<string, any>) {
    if (!this.currentUser) {
      console.error("No user is logged in.");
      return;
    }

    if (!monster || !monster.name) {
      console.error("Invalid monster data");
      return;
    }

    // Make sure the monster name is a valid document ID
    const validMonsterName = FirestoreService.prepMonsterName(monster.name);

    try {
      // Validate user ID first
      if (!this.currentUser.uid) {
        throw new Error("User ID is not available");
      }

      // Reference to the monsters subcollection
      const monstersCollectionRef = collection(db, "users", this.currentUser.uid, "monsters");

      // Serialize monster data before creating document reference
      const monsterData = serializeFn(monster);
      
      // Clean up any undefined values that could cause Firebase errors
      const cleanedData: Record<string, any> = {};
      Object.entries(monsterData).forEach(([key, value]) => {
        if (value !== undefined) {
          cleanedData[key] = value;
        }
      });
      
      // Create document reference and save data
      const monsterDocRef = doc(monstersCollectionRef, validMonsterName);
      // Use merge: false to completely replace the document, ensuring deleted/cleared fields are removed
      await setDoc(monsterDocRef, cleanedData, { merge: false });

      console.log("Monster saved successfully.");
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error("Error saving monster data:", errorMessage);
      alert(`Could not save monster: ${errorMessage}`);
    }
  };

  /**
   * Clear the Firestore snapshot listener.
   */
  clearSnapshotListener(): void {
    if (this.unsubscribeUserSnapshot) {
      this.unsubscribeUserSnapshot();
      this.unsubscribeUserSnapshot = null;
    }
    if (this.unsubscribeMonstersSnapshot) {
      this.unsubscribeMonstersSnapshot();
      this.unsubscribeMonstersSnapshot = null;
    }
  }
}

export const firestoreService = new FirestoreService();